import { useEffect, useState } from 'react'
import { Snowflake, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

/**
 * #444: заметное подтверждение, что пауза программы включена.
 *
 * Пауза уже влияла на всё — куратор не ждёт отчётов, жизни не снимаются,
 * напоминания молчат, — но в приложении о ней говорила только узкая плашка
 * вверху экрана. Люди её не замечали и писали в поддержку «а заморозка вообще
 * включилась?».
 *
 * Поэтому при первом заходе за сессию показываем карточку с датой окончания, а
 * дальше висит постоянный чип: тот же язык, что и у заморозки серии, чтобы
 * человек не разбирался в двух разных индикаторах.
 *
 * ЧТО ЭТО НЕ ДЕЛАЕТ: не блокирует приложение. Пауза покупается со словами
 * «куратор не тревожит, жизни не снимаются» — человек вправе продолжать вести
 * дневник. Блокирующий экран из ТЗ поменял бы это обещание, поэтому вынесен
 * отдельным вопросом.
 */

const SESSION_KEY = 'tfa.programPauseNoticeShown'

function formatUntil(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long' })
}

function daysLeft(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)))
}

export function ProgramPauseNotice({ pauseUntil }: { pauseUntil: string | null }) {
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!pauseUntil) return
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') return
      window.sessionStorage.setItem(SESSION_KEY, '1')
    } catch { /* приватный режим — покажем карточку каждый раз, это не страшно */ }
    setModalOpen(true)
  }, [pauseUntil])

  // Пауза кончилась — сбрасываем метку, чтобы следующая снова объявила о себе.
  useEffect(() => {
    if (pauseUntil) return
    try { window.sessionStorage.removeItem(SESSION_KEY) } catch { /* ignore */ }
  }, [pauseUntil])

  if (!pauseUntil) return null

  const left = daysLeft(pauseUntil)

  return (
    <>
      <motion.button
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        data-testid="program-pause-chip"
        onClick={() => setModalOpen(true)}
        aria-label={`Пауза программы до ${formatUntil(pauseUntil)}`}
        style={{
          position: 'fixed',
          // Над TabBar (300), ниже модалок (410+). Слева, чтобы не столкнуться
          // с чипом заморозки серии — он прижат к правому краю.
          zIndex: 320,
          left: 14,
          bottom: 'calc(112px + env(safe-area-inset-bottom, 0px))',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 12px 8px 10px',
          borderRadius: 22,
          background: 'linear-gradient(135deg, #5AA9F0 0%, #8E7BF5 100%)',
          border: '1.5px solid rgba(255,255,255,0.55)',
          color: 'white', cursor: 'pointer',
          fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 800,
          boxShadow: '0 8px 22px rgba(90,169,240,0.45)',
        }}
      >
        <Snowflake size={16} color="white" />
        Пауза · {left} дн.
      </motion.button>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 420,
              background: 'rgba(12,22,38,0.55)',
              backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 24, fontFamily: 'Roboto, sans-serif',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="program-pause-modal"
              style={{
                width: '100%', maxWidth: 360, borderRadius: 24, padding: '28px 22px 22px',
                background: 'linear-gradient(160deg, #EAF4FF 0%, #F3F0FF 100%)',
                border: '1px solid rgba(255,255,255,0.7)',
                boxShadow: '0 24px 60px rgba(20,40,80,0.28)',
                position: 'relative', textAlign: 'center',
              }}
            >
              <button
                onClick={() => setModalOpen(false)}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 12, right: 12, width: 32, height: 32,
                  borderRadius: '50%', border: 'none', background: 'rgba(28,45,65,0.08)',
                  color: '#1C2D41', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={16} />
              </button>

              <div style={{
                width: 64, height: 64, margin: '0 auto 14px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #5AA9F0, #8E7BF5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 26px rgba(90,169,240,0.45)',
              }}>
                <Snowflake size={30} color="white" />
              </div>

              <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 900, color: '#1C2D41' }}>
                Заморозка активна до {formatUntil(pauseUntil)}
              </h2>
              <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.5, color: '#5A6B80' }}>
                Осталось {left} {left === 1 ? 'день' : left < 5 ? 'дня' : 'дней'}. Пока идёт пауза:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', marginBottom: 18 }}>
                {[
                  'Жизни не снимаются, серия сохраняется',
                  'Куратор не ждёт отчётов и не ставит нарушения',
                  'Напоминания не приходят',
                  'Доступ к программе продлевается на дни паузы',
                ].map((line) => (
                  <div key={line} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '10px 12px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.7)',
                    fontSize: 13.5, color: '#1C2D41', lineHeight: 1.4,
                  }}>
                    <Snowflake size={15} color="#5AA9F0" style={{ flexShrink: 0, marginTop: 1 }} />
                    {line}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setModalOpen(false)}
                style={{
                  width: '100%', height: 48, borderRadius: 14, border: 'none',
                  background: 'linear-gradient(135deg, #5AA9F0, #8E7BF5)',
                  color: 'white', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                }}
              >
                Понятно
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
